import { FC, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import { PopoverContent } from '@new/common/ui/Popover/Popover';
import { NotificationsList } from '../NotificationsList/NotificationsList';
import { useGetNotificationsQuery, useMarkNotificationAsReadMutation } from '@teachur/http-client';
import { toast } from 'react-toastify';

interface NotificationsModalProps {
  onClose: () => void;
}

export const NotificationsModal: FC<NotificationsModalProps> = ({ onClose }) => {
  const { data, isLoading } = useGetNotificationsQuery();
  const { mutate: markAsRead } = useMarkNotificationAsReadMutation({
    onError: () => toast.error('Failed to mark notification as read'),
  });

  const unreadNotifications = useMemo(
    () => (data?.notifications || []).filter((n) => !n.read),
    [data?.notifications]
  );

  return (
    <PopoverContent
      className="flex w-[calc(100vw-32px)] max-w-99.25 p-0 pt-5 shadow-xs! flex-col items-start gap-4 rounded-2xl border border-(--neutral-black-NB100) bg-(--neutral-white-NW900)"
      align="end"
      sideOffset={8}
    >
      <div className="flex px-4 py-0 justify-between items-center self-stretch">
        <span
          className="text-(--neutral-black-NB1000) text-[18px] font-bold leading-[125%]"
          style={{ fontFamily: 'Arvo' }}
        >
          Notifications
        </span>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-full! transition-colors cursor-pointer"
          aria-label="Close notifications"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto self-stretch max-h-112.5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : (
          <NotificationsList
            notifications={unreadNotifications}
            onMarkAsRead={markAsRead}
            emptyMessage="No unread notifications"
          />
        )}
      </div>
    </PopoverContent>
  );
};
